import type { Locale } from '../../../types';

/**
 * In-code translations for copy the Form component owns. Contentful-authored copy (submit label,
 * success message, the privacy disclaimer) is localized in Contentful instead.
 */
export type LocalizedText = Record<Locale, string>;

/**
 * Keyed by field `id`, not `fieldName`: COMPANY and MEDIA_OUTLET share the `company` fieldName
 * while showing different labels. A field missing here falls back to the `label` on its definition
 * in constants.ts, so keep the English values in sync with those.
 */
export const FIELD_LABELS: Record<string, LocalizedText> = {
  'first-name': { 'en-US': 'First Name', ja: '名', ko: '이름', 'zh-CN': '名字' },
  'last-name': { 'en-US': 'Last Name', ja: '姓', ko: '성', 'zh-CN': '姓氏' },
  company: { 'en-US': 'Company', ja: '会社名', ko: '회사명', 'zh-CN': '公司名' },
  email: { 'en-US': 'Email', ja: 'メールアドレス', ko: '업무 이메일', 'zh-CN': '工作邮箱' },
  'job-title': { 'en-US': 'Job Title', ja: '役職', ko: '직책', 'zh-CN': '职位' },
  phone: { 'en-US': 'Mobile Phone', ja: '電話番号', ko: '연락처', 'zh-CN': '电话' },
  'product-interest': {
    'en-US': 'Product Interest',
    ja: '興味・関心のある製品をお選びください。',
    ko: '관심 있는 상품을 선택해 주세요',
    'zh-CN': '您感兴趣的产品是什么？',
  },
  'type-of-request': {
    'en-US': 'Type of Request',
    ja: 'リクエストの種類',
    ko: '문의 유형',
    'zh-CN': '咨询类型',
  },
  'media-outlet': {
    'en-US': 'Name of Media Outlet',
    ja: 'メディア名',
    ko: '매체명',
    'zh-CN': '媒体名称',
  },
  question: {
    'en-US': 'Question / Inquiry',
    ja: 'ご質問・お問い合わせ内容',
    ko: '질문 / 문의',
    'zh-CN': '咨询',
  },
};

/** Placeholders for the select controls, keyed by field `id` like FIELD_LABELS. */
export const FIELD_PLACEHOLDERS: Record<string, LocalizedText> = {
  'product-interest': { 'en-US': 'Select Product', ja: '製品を選択', ko: '제품 선택', 'zh-CN': '选择产品' },
  'type-of-request': { 'en-US': 'Please select…', ja: '選択してください', ko: '선택해 주세요', 'zh-CN': '请选择' },
};

/**
 * Display labels for select options, keyed by the exact English option value. That value is what
 * `<option value>` submits and what reaches Salesforce as a picklist key, so it is never localized
 * — only the visible text is. An option missing here displays its English value.
 */
export const OPTION_LABELS: Record<string, LocalizedText> = {
  'Not sure': { 'en-US': 'Not sure', ja: 'まだ決まっていない', ko: '아직 확실하지 않음', 'zh-CN': '需要产品建议' },
  'Mobile App Data': {
    'en-US': 'Mobile App Data',
    ja: 'モバイルアプリのデータ',
    ko: '모바일 앱 데이터',
    'zh-CN': '移动应用数据',
  },
  'Digital Advertising Data / Retail Media (Pathmatics)': {
    'en-US': 'Digital Advertising Data / Retail Media (Pathmatics)',
    ja: 'デジタル広告のデータ / リテールメディア (Pathmatics)',
    ko: '디지털 광고 데이터 / 리테일 미디어(패스매틱스)',
    'zh-CN': '数字广告数据 / 零售媒体数据',
  },
  'Audience Insights': {
    'en-US': 'Audience Insights',
    ja: 'Audience Insights',
    ko: '오디언스 인사이트',
    'zh-CN': '受众洞察',
  },
  'Web Insights': {
    'en-US': 'Web Insights',
    ja: 'Web Insights',
    ko: '웹 인사이트',
    'zh-CN': '网站洞察',
  },
  'Video Game Insights (PC / Console data)': {
    'en-US': 'Video Game Insights (PC / Console data)',
    ja: 'Video Game Insights (PC / Console data)',
    ko: '비디오 게임 인사이트',
    'zh-CN': 'PC/主机数据',
  },
  Playliner: {
    'en-US': 'Playliner',
    ja: 'Playliner',
    ko: '플레이라이너',
    'zh-CN': '运营活动洞察',
  },
};

/**
 * `{max}` interpolates the field's maxLength. No message names its field: each renders directly
 * beneath the field it belongs to and is linked by aria-describedby.
 */
export const VALIDATION_MESSAGES = {
  required: {
    'en-US': 'This field is required.',
    ja: 'このフィールドは必須です',
    ko: '필수 입력 항목입니다',
    'zh-CN': '此项为必填项',
  },
  consentRequired: {
    'en-US': 'Please accept to continue.',
    ja: '続行するには同意してください',
    ko: '계속하려면 동의해 주세요',
    'zh-CN': '请勾选同意后继续',
  },
  maxLength: {
    'en-US': 'Must be {max} characters or less',
    ja: '{max}文字以内で入力してください',
    ko: '{max}자 이하로 입력해 주세요',
    'zh-CN': '不能超过 {max} 个字符',
  },
  invalidEmail: {
    'en-US': 'Please enter a valid email address',
    ja: '有効なメールアドレスを入力してください',
    ko: '유효한 이메일 주소를 입력해 주세요',
    'zh-CN': '请输入有效的电子邮箱地址',
  },
  nonBusinessEmail: {
    'en-US': 'Please enter a business email address',
    ja: 'ビジネス用のメールアドレスをご使用ください。',
    ko: '유효한 기업용 이메일을 입력해 주세요',
    'zh-CN': '请输入有效的企业邮箱',
  },
  invalidPhone: {
    'en-US': 'Can only contain numbers, spaces, + and -',
    ja: '数字、スペース、+、- のみ使用できます',
    ko: '숫자, 공백, + 및 - 만 사용할 수 있습니다',
    'zh-CN': '只能包含数字、空格、+ 和 -',
  },
} satisfies Record<string, LocalizedText>;

export type ValidationMessageKey = keyof typeof VALIDATION_MESSAGES;

/**
 * Messages for the submission request itself — pending, failed, throttled. Unlike
 * VALIDATION_MESSAGES these have no field to sit under; they render at the submit button.
 * The ja/ko/zh-CN copy has not been through APAC review yet.
 */
export const SUBMIT_MESSAGES = {
  submitting: {
    'en-US': 'Submitting…',
    ja: '送信中…',
    ko: '제출 중…',
    'zh-CN': '提交中…',
  },
  // Generic on purpose: a 4xx from our own payload bug, a 5xx, and a dropped connection all
  // look the same to the visitor, and none of them recorded anything — "try again" is accurate
  // for every case.
  submitFailed: {
    'en-US': 'Something went wrong. Please try again.',
    ja: '送信に失敗しました。もう一度お試しください。',
    ko: '문제가 발생했습니다. 다시 시도해 주세요.',
    'zh-CN': '提交失败，请重试。',
  },
  // HTTP 429 — the visitor hit the endpoint's per-IP throttle, so unlike submitFailed an
  // immediate retry will not help; the copy has to say to wait.
  submitRateLimited: {
    'en-US': 'Too many attempts. Please wait a few minutes and try again.',
    ja: '送信回数が上限に達しました。数分後にもう一度お試しください。',
    ko: '요청이 너무 많습니다. 몇 분 후에 다시 시도해 주세요.',
    'zh-CN': '尝试次数过多，请几分钟后重试。',
  },
} satisfies Record<string, LocalizedText>;

export type SubmitMessageKey = keyof typeof SUBMIT_MESSAGES;
